import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, X, Upload, GripVertical, ChevronLeft, ChevronRight, Star, ImageIcon, FileText, Youtube } from 'lucide-react';
import { categoriesApi } from '../../api/endpoints/categories';
import { productsApi } from '../../api/endpoints/products';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { getCdnUrl } from '../../lib/formatters';
import { rejectReasonFor, MAX_PRODUCT_IMAGES, MAX_VARIANT_IMAGES } from '../../lib/imageUpload';
import { useStore } from '../../contexts/StoreContext';
import type { CustomField, CustomFieldOption, FieldEvent, ProductSeo, VariantOption } from '../../types/product';

/* ------------------------------------------------------------------ */
/*  Image upload section                                               */
/* ------------------------------------------------------------------ */

interface ImagesSectionProps {
  images: string[];
  newImages: string[];
  onAddImage: (base64: string) => void;
  onRemoveExisting: (index: number) => void;
  onRemoveNew: (index: number) => void;
  onReorderExisting?: (from: number, to: number) => void;
}

export function ImagesSection({ images, newImages, onAddImage, onRemoveExisting, onRemoveNew, onReorderExisting }: ImagesSectionProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { currentStore } = useStore();
  const [dragOver, setDragOver] = useState(false);
  const [imageTab, setImageTab] = useState<'upload' | 'existing'>('upload');
  // Files rejected before upload (wrong type, over 10 MB) or whose upload
  // failed. Reported rather than dropped in silence, which is what the previous
  // 5 MB gate did.
  const [skippedCount, setSkippedCount] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Downscaled before base64 (see lib/imageUpload): the raw bytes of a few
  // phone photos overflow the server's 11 MB JSON body on their own.
  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || !storeId) return;
    // Counted down locally: onAddImage only appends, so selecting 30 files at
    // once would sail past the cap and get the whole save rejected by the API.
    let remaining = MAX_PRODUCT_IMAGES - (images.length + newImages.length);
    let skipped = 0;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (remaining <= 0) break;
        if (rejectReasonFor(file)) { skipped++; continue; }
        try {
          // Straight to the CDN; only the returned basename is kept.
          onAddImage(await productsApi.uploadImage(storeId, file));
          remaining--;
        } catch {
          skipped++;
        }
      }
    } finally {
      setUploading(false);
    }
    setSkippedCount(skipped);
  }, [onAddImage, storeId, images.length, newImages.length]);

  // Fetch recent product images from the store's products
  const { data: existingProductsData, isLoading: existingImagesLoading } = useQuery({
    queryKey: ['products', storeId, { page: 1, limit: 50 }],
    queryFn: () => productsApi.list(storeId!, { page: 1, limit: 50 }),
    enabled: !!storeId && imageTab === 'existing',
  });

  // Collect unique images from recent products
  const recentProductImages = (() => {
    if (!existingProductsData?.products) return [];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const p of existingProductsData.products) {
      for (const img of p.images ?? []) {
        if (!seen.has(img)) {
          seen.add(img);
          result.push(img);
        }
        if (result.length >= 30) break;
      }
      if (result.length >= 30) break;
    }
    return result;
  })();

  // Track which existing images are already selected (by filename) to avoid duplicates
  const alreadySelected = new Set([...images, ...newImages]);

  const allImages = [
    ...images.map((img, i) => ({ type: 'existing' as const, src: getCdnUrl(img, currentStore?._id), index: i })),
    ...newImages.map((img, i) => ({ type: 'new' as const, src: img, index: i })),
  ];

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">{t('common.images')}</h2>
        {images.length > 0 && (
          <p className="text-xs text-text-muted">{t('products.firstIsPreview', 'First image is the preview image')}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        {allImages.map((img, i) => (
          <div key={`${img.type}-${img.index}`} className="relative group">
            <img src={img.src} alt="" className={`h-24 w-24 rounded-lg object-cover border-2 ${i === 0 ? 'border-accent' : 'border-border-default'}`} />
            {i === 0 && (
              <div className="absolute top-1 start-1 rounded-full bg-accent p-0.5">
                <Star className="h-2.5 w-2.5 text-white fill-white" />
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 hidden group-hover:flex items-center justify-center gap-0.5 bg-black/50 rounded-b-lg py-0.5">
              {img.type === 'existing' && onReorderExisting && img.index > 0 && (
                <button type="button" onClick={() => onReorderExisting(img.index, img.index - 1)} className="text-white p-0.5">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
              )}
              {img.type === 'existing' && onReorderExisting && img.index < images.length - 1 && (
                <button type="button" onClick={() => onReorderExisting(img.index, img.index + 1)} className="text-white p-0.5">
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => img.type === 'existing' ? onRemoveExisting(img.index) : onRemoveNew(img.index)}
              className="absolute -top-2 -end-2 hidden group-hover:flex rounded-full bg-danger p-1 text-white hover:bg-danger/80"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Upload / Select existing toggle */}
      {allImages.length < MAX_PRODUCT_IMAGES && (
        <div className="mt-4">
          <div className="flex gap-1 rounded-lg bg-bg-tertiary p-1 mb-3 w-fit">
            <button
              type="button"
              onClick={() => setImageTab('upload')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                imageTab === 'upload'
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Upload className="inline-block h-3.5 w-3.5 me-1" />
              {t('products.uploadNew', 'Upload New')}
            </button>
            <button
              type="button"
              onClick={() => setImageTab('existing')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                imageTab === 'existing'
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <ImageIcon className="inline-block h-3.5 w-3.5 me-1" />
              {t('products.selectExisting', 'Select Existing')}
            </button>
          </div>

          {imageTab === 'upload' ? (
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              className={`flex h-24 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                dragOver ? 'border-accent bg-accent/5' : 'border-border-default hover:border-accent/50'
              }`}
            >
              <Upload className="h-5 w-5 text-text-muted" />
              <span className="text-xs text-text-muted">{t('products.dragOrClick', 'Drag & drop or click to upload')}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            </label>
          ) : (
            <div className="rounded-lg border border-border-default p-3">
              {existingImagesLoading ? (
                <div className="flex justify-center py-6">
                  <LoadingSpinner />
                </div>
              ) : recentProductImages.length === 0 ? (
                <p className="text-xs text-text-muted text-center py-4">{t('products.noExistingImages', 'No existing product images found')}</p>
              ) : (
                <>
                  <p className="text-xs text-text-muted mb-2">{t('products.selectFromRecent', 'Select from recent product images')}</p>
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                    {recentProductImages.map((imgPath) => {
                      const isAlreadyUsed = alreadySelected.has(imgPath);
                      return (
                        <button
                          key={imgPath}
                          type="button"
                          disabled={isAlreadyUsed}
                          onClick={() => {
                            if (!isAlreadyUsed) onAddImage(imgPath);
                          }}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            isAlreadyUsed
                              ? 'border-accent/50 opacity-50 cursor-not-allowed'
                              : 'border-border-default hover:border-accent cursor-pointer hover:shadow-md'
                          }`}
                        >
                          <img
                            src={getCdnUrl(imgPath, currentStore?._id)}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          {isAlreadyUsed && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Star className="h-3 w-3 text-white fill-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {uploading && (
            <p className="mt-2 flex items-center gap-2 text-xs text-text-muted">
              <LoadingSpinner />
              {t('products.uploadingImages', 'Uploading...')}
            </p>
          )}
          {skippedCount > 0 && (
            <p className="mt-2 text-xs text-danger">
              {t('products.imagesSkipped', '{{count}} file(s) skipped: not an image, or larger than 10MB.', { count: skippedCount })}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Product video                                                      */
/* ------------------------------------------------------------------ */

/**
 * Accepts every form YouTube's share sheet hands out (watch, youtu.be, embed,
 * shorts, live) plus Vimeo, and derives a preview thumbnail. Kept in sync with
 * the storefront's `lib/videoEmbed.ts`, which parses the same link again at
 * render time — so what the merchant previews here is what shoppers get.
 */
const YOUTUBE_RE = /(?:youtube\.com\/watch\?(?:[^#]*&)?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d+)/;

export function parseVideoUrl(url: string): { provider: 'youtube' | 'vimeo'; id: string; thumbnail: string | null } | null {
  if (!url) return null;
  const yt = url.match(YOUTUBE_RE);
  if (yt) return { provider: 'youtube', id: yt[1], thumbnail: `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg` };
  const vimeo = url.match(VIMEO_RE);
  if (vimeo) return { provider: 'vimeo', id: vimeo[1], thumbnail: null };
  return null;
}

interface VideoSectionProps {
  value: string;
  onChange: (value: string) => void;
}

export function VideoSection({ value, onChange }: VideoSectionProps) {
  const { t } = useTranslation();
  const trimmed = value.trim();
  const parsed = parseVideoUrl(trimmed);
  const invalid = trimmed.length > 0 && !parsed;

  return (
    <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-3">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <Youtube className="h-4 w-4 text-accent" />
        {t('products.productVideo', 'Product Video')}
      </h3>
      <Input
        id="videoUrl"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 500))}
        placeholder="https://www.youtube.com/watch?v=..."
        error={invalid ? t('products.videoUrlInvalid', 'Enter a valid YouTube or Vimeo link') : undefined}
      />
      {parsed ? (
        <div className="flex items-center gap-3">
          <div className="relative h-14 w-24 shrink-0 overflow-hidden rounded-md bg-black">
            {parsed.thumbnail && (
              <img src={parsed.thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
            )}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-6 w-8 items-center justify-center rounded bg-black/70">
                <svg viewBox="0 0 24 24" fill="#fff" className="h-3.5 w-3.5"><path d="M8 5v14l11-7z" /></svg>
              </span>
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-text-secondary">{t('products.videoFirstSlide', 'Shown as the first slide of the product gallery.')}</p>
            <button
              type="button"
              onClick={() => onChange('')}
              className="mt-1 text-xs text-text-muted hover:text-danger transition-colors"
            >
              {t('common.remove', 'Remove')}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-text-muted">{t('products.videoHint', 'Paste a YouTube link. It becomes the first slide of the product gallery on your storefront.')}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Category selection                                                 */
/* ------------------------------------------------------------------ */

interface CategoriesSectionProps {
  selected: string[];
  onChange: (categories: string[]) => void;
}

export function CategoriesSection({ selected, onChange }: CategoriesSectionProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newCatName, setNewCatName] = useState('');
  const [creating, setCreating] = useState(false);

  const { data } = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId,
  });

  const categories = data?.categories ?? [];

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(c => c !== id) : [...selected, id]);
  };

  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name || !storeId) return;
    setCreating(true);
    try {
      const result = await categoriesApi.create(storeId, { name });
      queryClient.invalidateQueries({ queryKey: ['categories', storeId] });
      // Auto-select the newly created category
      const newId = result.category?._id || result.categoryId;
      if (newId) onChange([...selected, newId]);
      setNewCatName('');
    } catch {
      // silent - toast handled elsewhere
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
      <h2 className="text-base font-semibold mb-4">{t('sidebar.categories')}</h2>
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.map((cat) => (
            <button
              key={cat._id}
              type="button"
              onClick={() => toggle(cat._id)}
              className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                selected.includes(cat._id)
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-border-default text-text-secondary hover:border-accent/30'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={newCatName}
          onChange={(e) => setNewCatName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createCategory(); } }}
          placeholder={t('categories.newCategoryPlaceholder', 'New category name...')}
          className="flex-1 rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
        />
        <Button type="button" variant="secondary" size="sm" onClick={createCategory} isLoading={creating} disabled={!newCatName.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Custom fields builder                                              */
/* ------------------------------------------------------------------ */

const FIELD_TYPES = ['text', 'bigText', 'selection', 'multiSelect', 'number', 'color'] as const;

/** Field types whose options the customer picks from (single value vs. several ticked). */
const OPTION_FIELD_TYPES: CustomField['fieldType'][] = ['selection', 'multiSelect'];

interface CustomFieldsSectionProps {
  fields: CustomField[];
  onChange: (fields: CustomField[]) => void;
  fieldsEvents?: FieldEvent[];
  onChangeEvents?: (events: FieldEvent[]) => void;
}

export function CustomFieldsSection({ fields, onChange, fieldsEvents = [], onChangeEvents }: CustomFieldsSectionProps) {
  const { t } = useTranslation();
  const [expandedVisibility, setExpandedVisibility] = useState<number | null>(null);

  const addField = () => {
    const id = Date.now();
    onChange([...fields, { id, title: '', fieldType: 'text', requirement: 'optional' }]);
  };

  const updateField = (index: number, patch: Partial<CustomField>) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...patch };
    onChange(updated);
  };

  const removeField = (index: number) => {
    const removedId = fields[index].id;
    onChange(fields.filter((_, i) => i !== index));
    // Also remove any visibility events referencing this field
    if (onChangeEvents) {
      onChangeEvents(fieldsEvents.filter(e => e.fieldId !== removedId && e.targetFieldId !== removedId));
    }
  };

  const addOption = (fieldIndex: number) => {
    const field = fields[fieldIndex];
    const options: CustomFieldOption[] = [...(field.options || []), { name: '' }];
    updateField(fieldIndex, { options });
  };

  const updateOption = (fieldIndex: number, optIndex: number, patch: Partial<CustomFieldOption>) => {
    const field = fields[fieldIndex];
    const options = [...(field.options || [])];
    options[optIndex] = { ...options[optIndex], ...patch };
    updateField(fieldIndex, { options });
  };

  const removeOption = (fieldIndex: number, optIndex: number) => {
    const field = fields[fieldIndex];
    const options = (field.options || []).filter((_, i) => i !== optIndex);
    updateField(fieldIndex, { options });
  };

  // Visibility events for a specific target field
  const getVisibilityEvent = (targetFieldId: number): FieldEvent | undefined =>
    fieldsEvents.find(e => e.targetFieldId === targetFieldId);

  const setVisibilityEvent = (targetFieldId: number, fieldId: number, value: string, action: 'show' | 'hide') => {
    if (!onChangeEvents) return;
    const existing = fieldsEvents.filter(e => e.targetFieldId !== targetFieldId);
    if (fieldId && value) {
      existing.push({ id: Date.now(), fieldId, value, action, targetFieldId });
    }
    onChangeEvents(existing);
  };

  const removeVisibilityEvent = (targetFieldId: number) => {
    if (!onChangeEvents) return;
    onChangeEvents(fieldsEvents.filter(e => e.targetFieldId !== targetFieldId));
  };

  // Option-based fields can serve as parent for conditional visibility / dependent options.
  // For a multiSelect parent the rule matches when the value is among the ticked options.
  const selectionFields = fields.filter(f => OPTION_FIELD_TYPES.includes(f.fieldType) && (f.options?.length ?? 0) > 0);

  const fieldTypeLabel = (ft: CustomField['fieldType']) => {
    switch (ft) {
      case 'text': return t('products.fieldTypeText', 'Text');
      case 'bigText': return t('products.fieldTypeBigText', 'Long text');
      case 'selection': return t('products.fieldTypeSelection', 'Dropdown');
      case 'multiSelect': return t('products.fieldTypeMultiSelect', 'Checkboxes');
      case 'number': return t('products.fieldTypeNumber', 'Number');
      case 'color': return t('products.fieldTypeColor', 'Color');
      default: return ft;
    }
  };

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">{t('products.customFields')}</h2>
        <Button type="button" variant="secondary" size="sm" onClick={addField}>
          <Plus className="h-4 w-4" /> {t('common.add')}
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-text-muted">{t('common.noData')}</p>
      ) : (
        <div className="space-y-4">
          {fields.map((field, fi) => {
            const visEvent = getVisibilityEvent(field.id);
            const isVisExpanded = expandedVisibility === field.id;

            return (
              <div key={field.id} className="rounded-lg border border-border-default/50 bg-bg-tertiary p-4 space-y-3">
                {/* Field header row */}
                <div className="flex items-start gap-3">
                  <GripVertical className="h-5 w-5 text-text-muted mt-2 shrink-0" />
                  {/* min-w-0 everywhere: inputs and selects otherwise refuse to shrink below their
                      intrinsic width and push the remove button outside the card. */}
                  <div className="flex-1 min-w-0 grid gap-3 sm:grid-cols-3">
                    <input
                      value={field.title}
                      onChange={(e) => updateField(fi, { title: e.target.value })}
                      placeholder={t('common.name')}
                      className="w-full min-w-0 rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
                    />
                    <select
                      value={field.fieldType}
                      onChange={(e) => updateField(fi, { fieldType: e.target.value as CustomField['fieldType'] })}
                      className="w-full min-w-0 truncate rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
                    >
                      {FIELD_TYPES.map((ft) => (
                        <option key={ft} value={ft}>{fieldTypeLabel(ft)}</option>
                      ))}
                    </select>
                    <select
                      value={field.requirement}
                      onChange={(e) => updateField(fi, { requirement: e.target.value as 'required' | 'optional' })}
                      className="w-full min-w-0 truncate rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
                    >
                      <option value="optional">{t('common.optional')}</option>
                      <option value="required">{t('common.required')}</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeField(fi)}
                    className="rounded-md p-1.5 text-text-muted hover:text-danger hover:bg-bg-hover shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Helper line shown under the field title on the storefront */}
                <div className="ms-8">
                  <input
                    value={field.description ?? ''}
                    onChange={(e) => updateField(fi, { description: e.target.value || undefined })}
                    placeholder={t('products.fieldDescription', 'Helper text under the title (optional)')}
                    title={t('products.fieldDescriptionHint', 'Write a link as [text](https://example.com)')}
                    className="w-full min-w-0 rounded-lg border border-border-default bg-bg-primary px-3 py-1.5 text-sm outline-none focus:border-accent"
                  />
                  <p className="mt-1 text-xs text-text-muted">
                    {t('products.fieldDescriptionHint', 'Write a link as [text](https://example.com)')}
                  </p>
                </div>

                {/* Selection / multiSelect options with price modifiers & dependent dropdown */}
                {OPTION_FIELD_TYPES.includes(field.fieldType) && (
                  <div className="ms-8 space-y-2">
                    {field.fieldType === 'multiSelect' && (
                      <p className="text-xs text-text-muted">
                        {t('products.multiSelectHint', 'Checkboxes: the customer can tick several options, each ticked one adds its price to the product')}
                      </p>
                    )}
                    {(field.options || []).map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input
                          value={opt.name}
                          onChange={(e) => updateOption(fi, oi, { name: e.target.value })}
                          placeholder={`${t('common.name')} ${oi + 1}`}
                          className="flex-1 min-w-0 rounded-lg border border-border-default bg-bg-primary px-3 py-1.5 text-sm outline-none focus:border-accent"
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={opt.priceModifier ?? ''}
                          onChange={(e) => updateOption(fi, oi, { priceModifier: e.target.value ? Number(e.target.value) : undefined })}
                          placeholder={t('products.priceModifier', '± Price')}
                          title={t('products.priceModifierHint', 'Positive to increase price, negative to decrease')}
                          className="w-20 sm:w-24 shrink-0 rounded-lg border border-border-default bg-bg-primary px-2 py-1.5 text-xs outline-none focus:border-accent text-center"
                        />
                        {/* Dependent dropdown: only show this option when a parent field has a specific value */}
                        {selectionFields.filter(sf => sf.id !== field.id).length > 0 && (
                          <select
                            value={opt.dependsOn ? `${opt.dependsOn.fieldId}:${opt.dependsOn.value}` : ''}
                            onChange={(e) => {
                              if (!e.target.value) {
                                updateOption(fi, oi, { dependsOn: undefined });
                              } else {
                                const [fid, val] = e.target.value.split(':');
                                updateOption(fi, oi, { dependsOn: { fieldId: Number(fid), value: val } });
                              }
                            }}
                            title={t('products.dependsOn', 'Show when...')}
                            className="w-24 sm:w-32 shrink-0 min-w-0 rounded-lg border border-border-default bg-bg-primary px-2 py-1.5 text-xs outline-none focus:border-accent truncate"
                          >
                            <option value="">{t('products.alwaysShow', 'Always')}</option>
                            {selectionFields
                              .filter(sf => sf.id !== field.id)
                              .flatMap(sf =>
                                (sf.options || []).map(so => (
                                  <option key={`${sf.id}:${so.name}`} value={`${sf.id}:${so.name}`}>
                                    {sf.title}: {so.name}
                                  </option>
                                ))
                              )}
                          </select>
                        )}
                        <button type="button" onClick={() => removeOption(fi, oi)} className="text-text-muted hover:text-danger shrink-0">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addOption(fi)}
                      className="text-xs text-accent hover:underline"
                    >
                      + {t('common.add')}
                    </button>
                  </div>
                )}

                {/* Conditional visibility rule */}
                {onChangeEvents && selectionFields.filter(sf => sf.id !== field.id).length > 0 && (
                  <div className="ms-8">
                    {visEvent ? (
                      <div className="flex items-center gap-2 rounded-md bg-accent/5 border border-accent/20 px-3 py-2 text-xs">
                        <span className="text-text-secondary">
                          {visEvent.action === 'show' ? t('products.showWhen', 'Show when') : t('products.hideWhen', 'Hide when')}{' '}
                          <strong>{fields.find(f => f.id === visEvent.fieldId)?.title || '?'}</strong> = <strong>{visEvent.value}</strong>
                        </span>
                        <button type="button" onClick={() => removeVisibilityEvent(field.id)} className="ms-auto text-text-muted hover:text-danger">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setExpandedVisibility(isVisExpanded ? null : field.id)}
                          className="text-xs text-text-muted hover:text-accent transition-colors"
                        >
                          {t('products.addVisibilityRule', '+ Visibility rule')}
                        </button>
                        {isVisExpanded && (
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            <select
                              defaultValue=""
                              id={`vis-action-${field.id}`}
                              className="rounded border border-border-default bg-bg-primary px-2 py-1 text-xs outline-none"
                            >
                              <option value="show">{t('products.showWhen', 'Show when')}</option>
                              <option value="hide">{t('products.hideWhen', 'Hide when')}</option>
                            </select>
                            <select
                              defaultValue=""
                              id={`vis-field-${field.id}`}
                              className="rounded border border-border-default bg-bg-primary px-2 py-1 text-xs outline-none"
                            >
                              <option value="" disabled>{t('products.selectField', 'Field...')}</option>
                              {selectionFields
                                .filter(sf => sf.id !== field.id)
                                .map(sf => <option key={sf.id} value={sf.id}>{sf.title}</option>)}
                            </select>
                            <span>=</span>
                            <input
                              id={`vis-value-${field.id}`}
                              placeholder={t('products.value', 'Value')}
                              className="w-24 rounded border border-border-default bg-bg-primary px-2 py-1 text-xs outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const action = (document.getElementById(`vis-action-${field.id}`) as HTMLSelectElement)?.value as 'show' | 'hide';
                                const fid = Number((document.getElementById(`vis-field-${field.id}`) as HTMLSelectElement)?.value);
                                const val = (document.getElementById(`vis-value-${field.id}`) as HTMLInputElement)?.value;
                                if (fid && val) {
                                  setVisibilityEvent(field.id, fid, val, action || 'show');
                                  setExpandedVisibility(null);
                                }
                              }}
                              className="rounded bg-accent px-2 py-1 text-white text-xs hover:bg-accent-hover"
                            >
                              {t('common.save')}
                            </button>
                            <button type="button" onClick={() => setExpandedVisibility(null)} className="text-text-muted hover:text-text-primary">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Tags section                                                       */
/* ------------------------------------------------------------------ */

interface TagsSectionProps {
  tags: string[];
  onChange: (tags: string[]) => void;
}

export function TagsSection({ tags, onChange }: TagsSectionProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
      <h2 className="text-base font-semibold mb-4">{t('products.tags', 'Tags')}</h2>
      <div className="flex flex-wrap gap-2 mb-3">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-accent/30 bg-accent/5 px-2.5 py-1 text-sm text-accent">
            {tag}
            <button type="button" onClick={() => removeTag(i)} className="text-accent/60 hover:text-danger">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          placeholder={t('products.addTag', 'Type a tag and press Enter')}
          className="flex-1 rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
        />
        <Button type="button" variant="secondary" size="sm" onClick={addTag}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  SEO section                                                        */
/* ------------------------------------------------------------------ */

interface SeoSectionProps {
  seo: ProductSeo;
  onChange: (seo: ProductSeo) => void;
  inline?: boolean;
}

export function SeoSection({ seo, onChange, inline }: SeoSectionProps) {
  const { t } = useTranslation();

  const content = (
    <div className="space-y-4">
      {!inline && <h2 className="text-base font-semibold mb-4">{t('products.seo')}</h2>}
      {inline && <p className="text-sm font-medium text-text-secondary pt-2 border-t border-border-default/50">{t('products.seo')}</p>}
      <Input
        label={t('settings.metaTitle')}
        value={seo.title ?? ''}
        onChange={(e) => onChange({ ...seo, title: e.target.value })}
      />
      <div>
        <label className="block text-sm text-text-secondary mb-1.5">{t('settings.metaDescription')}</label>
        <textarea
          rows={3}
          value={seo.description ?? ''}
          onChange={(e) => onChange({ ...seo, description: e.target.value })}
          className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent"
        />
      </div>
      <Input
        label={t('settings.keywords')}
        value={seo.keywords ?? ''}
        onChange={(e) => onChange({ ...seo, keywords: e.target.value })}
        placeholder="keyword1, keyword2, ..."
      />
    </div>
  );

  if (inline) return content;

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
      {content}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Variants builder                                                   */
/* ------------------------------------------------------------------ */

interface VariantsSectionProps {
  variantTypes: import('../../types/product').VariantType[];
  variants: import('../../types/product').Variant[];
  onChangeTypes: (types: import('../../types/product').VariantType[]) => void;
  onChangeVariants: (variants: import('../../types/product').Variant[]) => void;
}

export function VariantsSection({ variantTypes, variants, onChangeTypes, onChangeVariants }: VariantsSectionProps) {
  const { t } = useTranslation();
  const { currentStore } = useStore();
  const { storeId } = useParams<{ storeId: string }>();

  const addVariantType = () => {
    const id = `vt_${Date.now()}`;
    onChangeTypes([...variantTypes, { id, name: '', displayType: 'dropdown', options: [] }]);
  };

  const updateType = (index: number, patch: Partial<import('../../types/product').VariantType>) => {
    const updated = [...variantTypes];
    updated[index] = { ...updated[index], ...patch };
    onChangeTypes(updated);
    // Regenerate variant combinations when options change
    if (patch.options) regenerateCombinations(updated);
  };

  const removeType = (index: number) => {
    const updated = variantTypes.filter((_, i) => i !== index);
    onChangeTypes(updated);
    regenerateCombinations(updated);
  };

  const addOption = (typeIndex: number) => {
    const type = variantTypes[typeIndex];
    const id = `vo_${Date.now()}`;
    updateType(typeIndex, { options: [...type.options, { id, name: '' }] });
  };

  const updateOption = (typeIndex: number, optIndex: number, patch: Partial<VariantOption>) => {
    const type = variantTypes[typeIndex];
    const options = [...type.options];
    options[optIndex] = { ...options[optIndex], ...patch };
    updateType(typeIndex, { options });
  };

  const removeOption = (typeIndex: number, optIndex: number) => {
    const type = variantTypes[typeIndex];
    const options = type.options.filter((_, i) => i !== optIndex);
    updateType(typeIndex, { options });
  };

  const handleVariantImage = (variantIndex: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async () => {
      const files = input.files;
      if (!files || !storeId) return;
      // Uploaded first, then written in ONE update. The original started a
      // FileReader per file and each onload applied its result to the `variants`
      // value it had closed over, so selecting several images kept only
      // whichever finished last.
      const existing = variants[variantIndex]?.images ?? [];
      const added: string[] = [];
      for (const file of Array.from(files)) {
        if (existing.length + added.length >= MAX_VARIANT_IMAGES) break;
        if (rejectReasonFor(file)) continue;
        try {
          // A filename, not base64. Variant images used to be stored as data
          // URLs inline on the product document in Mongo; this stops that.
          added.push(await productsApi.uploadImage(storeId, file));
        } catch { /* skip this file, keep the rest */ }
      }
      if (added.length === 0) return;
      const updated = [...variants];
      updated[variantIndex] = { ...updated[variantIndex], images: [...existing, ...added] };
      onChangeVariants(updated);
    };
    input.click();
  };

  const removeVariantImage = (variantIndex: number, imgIndex: number) => {
    const updated = [...variants];
    const imgs = [...(updated[variantIndex].images ?? [])];
    imgs.splice(imgIndex, 1);
    updated[variantIndex] = { ...updated[variantIndex], images: imgs.length > 0 ? imgs : undefined };
    onChangeVariants(updated);
  };

  const setDefaultVariant = (variantIndex: number) => {
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === variantIndex }));
    onChangeVariants(updated);
  };

  const regenerateCombinations = (types: import('../../types/product').VariantType[]) => {
    const validTypes = types.filter(t => t.options.length > 0);
    if (validTypes.length === 0) { onChangeVariants([]); return; }

    // Build all combinations
    const combos: Record<string, string>[][] = [[]];
    for (const vt of validTypes) {
      const newCombos: Record<string, string>[][] = [];
      for (const combo of combos) {
        for (const opt of vt.options) {
          if (!opt.name.trim()) continue;
          newCombos.push([...combo, { [vt.id]: opt.id }]);
        }
      }
      combos.length = 0;
      combos.push(...newCombos);
    }

    const newVariants = combos.map((combo) => {
      const combination = combo.reduce((acc, c) => ({ ...acc, ...c }), {});
      const key = JSON.stringify(combination);
      const existing = variants.find(v => JSON.stringify(v.combination) === key);
      return existing ?? {
        id: `v_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        combination,
        isActive: true,
      };
    });
    onChangeVariants(newVariants);
  };

  const updateVariant = (index: number, patch: Partial<import('../../types/product').Variant>) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], ...patch };
    onChangeVariants(updated);
  };

  const getOptionName = (typeId: string, optionId: string): string => {
    const type = variantTypes.find(t => t.id === typeId);
    return type?.options.find(o => o.id === optionId)?.name ?? optionId;
  };

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">{t('products.variants')}</h2>
        <Button type="button" variant="secondary" size="sm" onClick={addVariantType}>
          <Plus className="h-4 w-4" /> {t('variants.addType', 'Add Option Type')}
        </Button>
      </div>

      {variantTypes.length === 0 ? (
        <p className="text-sm text-text-muted">{t('variants.noTypes', 'No variant types. Add one to create product variants (e.g., Size, Color).')}</p>
      ) : (
        <div className="space-y-4">
          {/* Variant types */}
          {variantTypes.map((vt, ti) => (
            <div key={vt.id} className="rounded-lg border border-border-default/50 bg-bg-tertiary p-4 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  value={vt.name}
                  onChange={(e) => updateType(ti, { name: e.target.value })}
                  placeholder={t('variants.typeName', 'Type name (e.g., Size, Color)')}
                  className="flex-1 min-w-0 rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
                />
                <select
                  value={vt.displayType}
                  onChange={(e) => updateType(ti, { displayType: e.target.value as 'dropdown' | 'buttons' | 'swatches' })}
                  className="w-28 sm:w-36 shrink-0 min-w-0 truncate rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
                >
                  <option value="dropdown">{t('variants.dropdown', 'Dropdown')}</option>
                  <option value="buttons">{t('variants.buttons', 'Buttons')}</option>
                  <option value="swatches">{t('variants.swatches', 'Swatches')}</option>
                </select>
                <button type="button" onClick={() => removeType(ti)} className="shrink-0 text-text-muted hover:text-danger p-1">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Options */}
              <div className="ms-2 space-y-2">
                {vt.options.map((opt, oi) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <input
                      value={opt.name}
                      onChange={(e) => updateOption(ti, oi, { name: e.target.value })}
                      placeholder={`${t('variants.optionName', 'Option')} ${oi + 1}`}
                      className="flex-1 min-w-0 rounded-lg border border-border-default bg-bg-primary px-3 py-1.5 text-sm outline-none focus:border-accent"
                    />
                    {vt.displayType === 'swatches' && (
                      <input
                        type="color"
                        value={opt.colorHex || '#000000'}
                        onChange={(e) => updateOption(ti, oi, { colorHex: e.target.value })}
                        title={t('variants.swatchColor', 'Swatch color')}
                        className="h-8 w-8 shrink-0 cursor-pointer rounded border border-border-default bg-bg-primary p-0.5"
                      />
                    )}
                    <button type="button" onClick={() => removeOption(ti, oi)} className="shrink-0 text-text-muted hover:text-danger">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addOption(ti)} className="text-xs text-accent hover:underline">
                  + {t('variants.addOption', 'Add Option')}
                </button>
              </div>
            </div>
          ))}

          {/* Variant combinations table */}
          {variants.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">{t('variants.combinations', 'Combinations')} ({variants.length})</h3>
              <div className="overflow-x-auto rounded-lg border border-border-default">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border-default bg-bg-tertiary text-text-muted">
                      <th className="px-3 py-2 text-start font-medium">{t('variants.variant', 'Variant')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('variants.priceModifier', 'Price +/-')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('variants.absolutePrice', 'Price')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('common.quantity')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('products.sku')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('common.images')}</th>
                      <th className="px-3 py-2 text-start font-medium w-16">{t('variants.default', 'Default')}</th>
                      <th className="px-3 py-2 text-start font-medium w-16">{t('coupons.active')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v, vi) => {
                      const label = Object.entries(v.combination)
                        .map(([typeId, optId]) => getOptionName(typeId, optId))
                        .join(' / ');
                      return (
                        <tr key={v.id} className="border-b border-border-default/50 last:border-0">
                          <td className="px-3 py-2 font-medium">{label || '-'}</td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={v.priceModifier ?? ''}
                              onChange={(e) => updateVariant(vi, { priceModifier: e.target.value ? Number(e.target.value) : undefined })}
                              placeholder="±"
                              className="w-24 rounded border border-border-default bg-bg-primary px-2 py-1 text-sm outline-none focus:border-accent"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={v.price ?? ''}
                              onChange={(e) => updateVariant(vi, { price: e.target.value ? Number(e.target.value) : undefined })}
                              placeholder={t('variants.overridePrice', '-')}
                              title={t('variants.absolutePriceHint', 'Set an exact price for this variant (overrides base price + modifier)')}
                              className="w-24 rounded border border-border-default bg-bg-primary px-2 py-1 text-sm outline-none focus:border-accent"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              value={v.quantity ?? ''}
                              onChange={(e) => updateVariant(vi, { quantity: e.target.value ? Number(e.target.value) : undefined })}
                              className="w-20 rounded border border-border-default bg-bg-primary px-2 py-1 text-sm outline-none focus:border-accent"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={v.sku ?? ''}
                              onChange={(e) => updateVariant(vi, { sku: e.target.value || undefined })}
                              className="w-28 rounded border border-border-default bg-bg-primary px-2 py-1 text-sm outline-none focus:border-accent"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              {(v.images ?? []).map((img, ii) => (
                                <div key={ii} className="relative group/img">
                                  <img
                                    src={img.startsWith('data:') ? img : getCdnUrl(img, currentStore?._id)}
                                    alt=""
                                    className="h-8 w-8 rounded object-cover border border-border-default"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeVariantImage(vi, ii)}
                                    className="absolute -top-1 -end-1 hidden group-hover/img:flex rounded-full bg-danger p-0.5 text-white"
                                  >
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                              {(v.images ?? []).length < MAX_VARIANT_IMAGES && (
                                <button
                                  type="button"
                                  onClick={() => handleVariantImage(vi)}
                                  title={t('variants.addImage', 'Add image')}
                                  className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-border-default text-text-muted hover:border-accent hover:text-accent transition-colors"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="radio"
                              name="defaultVariant"
                              checked={v.isDefault === true}
                              onChange={() => setDefaultVariant(vi)}
                              className="accent-accent"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Toggle checked={v.isActive} onChange={(val) => updateVariant(vi, { isActive: val })} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Digital file upload                                                */
/* ------------------------------------------------------------------ */

interface DigitalFileSectionProps {
  storeId: string;
  productId: string;
  currentFile?: string;
  onFileUploaded: (fileName: string) => void;
}

export function DigitalFileSection({ storeId, productId, currentFile, onFileUploaded }: DigitalFileSectionProps) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { productsApi } = await import('../../api/endpoints/products');
      // Upload through the backend (same-origin) — it forwards to the CDN and
      // links the file to the product. No browser->CDN CORS to worry about.
      const { fileName } = await productsApi.uploadDigitalFile(storeId, productId, file);
      onFileUploaded(fileName);
      setUploading(false);
    } catch {
      setError(t('common.error'));
      setUploading(false);
    }
  };

  // Stored value is a CDN path like
  // `stores/data/<storeId>/digitalFiles/<10charRandom>-<originalName>`.
  // Show the merchant the original filename, not the internal path/prefix.
  const displayName = currentFile
    ? (currentFile.split('/').pop() || currentFile).replace(/^[a-zA-Z0-9]{10}-/, '')
    : '';

  return (
    <div className="space-y-3">
      {currentFile && (
        <div className="flex items-center gap-3 rounded-lg border border-border-default bg-bg-tertiary p-3">
          <FileText className="h-5 w-5 shrink-0 text-accent" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-text-muted">{t('products.currentFile', 'Current file')}</p>
            <p className="text-sm font-medium truncate">{displayName}</p>
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 rounded-lg border border-dashed border-border-default px-4 py-3 text-sm text-text-muted hover:border-accent/50 hover:text-accent transition-colors cursor-pointer">
        <Upload className="h-4 w-4" />
        {uploading
          ? t('common.loading')
          : currentFile
            ? t('products.replaceDigitalFile', 'Replace file')
            : t('products.uploadDigitalFile', 'Upload Digital File')}
        <input
          type="file"
          className="hidden"
          disabled={uploading}
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
        />
      </label>
      <p className="text-xs text-text-muted">
        {t('products.replaceDigitalFileHint', 'Uploading a new file replaces the current one for future orders.')}
      </p>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Additional settings                                                */
/* ------------------------------------------------------------------ */

interface SettingsSectionProps {
  minQuantity?: number;
  maxQuantity?: number;
  showQuantity: boolean;
  allowReviews: boolean;
  onMinQtyChange: (v: number | undefined) => void;
  onMaxQtyChange: (v: number | undefined) => void;
  onShowQtyChange: (v: boolean) => void;
  onAllowReviewsChange: (v: boolean) => void;
}

export function AdditionalSettingsSection({
  minQuantity, maxQuantity, showQuantity, allowReviews,
  onMinQtyChange, onMaxQtyChange, onShowQtyChange, onAllowReviewsChange,
}: SettingsSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
      <h2 className="text-base font-semibold mb-4">{t('common.settings')}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('products.minQuantity')}
          type="number"
          value={minQuantity ?? ''}
          onChange={(e) => onMinQtyChange(e.target.value ? Number(e.target.value) : undefined)}
        />
        <Input
          label={t('products.maxQuantity')}
          type="number"
          value={maxQuantity ?? ''}
          onChange={(e) => onMaxQtyChange(e.target.value ? Number(e.target.value) : undefined)}
        />
      </div>
      <Toggle
        label={t('products.showQuantity')}
        description={t('products.showQuantityDesc')}
        checked={showQuantity}
        onChange={onShowQtyChange}
      />
      <Toggle
        label={t('products.allowReviews')}
        description={t('products.allowReviewsDesc')}
        checked={allowReviews}
        onChange={onAllowReviewsChange}
      />
    </section>
  );
}
